import moment from 'moment';

const as_moment = (raw) => {
    if (raw == null || raw === '')
        return null;
    const m = moment.isMoment(raw) ? raw.clone() : moment(raw);
    return m.isValid() ? m : null;
};

/**
 * Visible calendar window from react-big-calendar `onRangeChange`.
 * Week/day: Date[]. Month: { start, end }.
 *
 * @returns {{ start: moment.Moment, end: moment.Moment }|null}
 */
export const screenings_calendar_visible_bounds = (range) => {
    if (Array.isArray(range) && range.length > 0) {
        const start = as_moment(range[0]);
        const end = as_moment(range[range.length - 1]);
        if (!start || !end)
            return null;
        return {
            start: start.startOf('day'),
            end: end.endOf('day'),
        };
    }

    if (range && range.start != null && range.end != null) {
        const start = as_moment(range.start);
        const end = as_moment(range.end);
        if (!start || !end)
            return null;
        return {
            start: start.startOf('day'),
            end: end.endOf('day'),
        };
    }

    return null;
};

/**
 * Overlap query for the *calendar viewport* (not the list date filter):
 * stop_at >= visible start AND start_at <= visible end.
 *
 * @returns {{ stop_at_gte: string, start_at_lte: string }|null}
 */
export const screenings_calendar_range_api_params = (range) => {
    const bounds = screenings_calendar_visible_bounds(range);
    if (!bounds)
        return null;
    return {
        stop_at_gte: bounds.start.toISOString(),
        start_at_lte: bounds.end.toISOString(),
    };
};

/**
 * Fallback range before `onRangeChange` (ISO week = Monday–Sunday).
 *
 * @param {Date|string|moment.Moment} date
 * @param {string} view
 * @returns {{ start: Date, end: Date }}
 */
export const screenings_calendar_view_range = (date, view) => {
    const m = as_moment(date) || moment();
    if (view === 'month') {
        return {
            start: m.clone().startOf('month').startOf('isoWeek').toDate(),
            end: m.clone().endOf('month').endOf('isoWeek').toDate(),
        };
    }
    if (view === 'day') {
        return {
            start: m.clone().startOf('day').toDate(),
            end: m.clone().endOf('day').toDate(),
        };
    }
    return {
        start: m.clone().startOf('isoWeek').toDate(),
        end: m.clone().endOf('isoWeek').toDate(),
    };
};

/** Same 08:00 fallback as `/plan` (`planInitialDate`). */
const SCROLL_FALLBACK_HOUR = 8;

const event_starts = (events) => (events || [])
    .map((event) => as_moment(event?.start))
    .filter(Boolean)
    .sort((a, b) => a.valueOf() - b.valueOf());

/**
 * Day to open: keep `from` when it has a screening, else the next one, else the first loaded.
 *
 * @param {Array<{ start?: Date }>} events
 * @param {Date|string|moment.Moment} [from]
 * @returns {Date}
 */
export const screenings_calendar_focus_date = (events, from = new Date()) => {
    const origin = (as_moment(from) || moment()).startOf('day');
    const starts = event_starts(events);
    if (!starts.length)
        return origin.toDate();
    if (starts.some((start) => start.isSame(origin, 'day')))
        return origin.toDate();
    const after = starts.find((start) => start.isAfter(origin, 'day'));
    return (after || starts[0]).clone().startOf('day').toDate();
};

/**
 * Initial calendar day when switching from the list: prefer the next loaded
 * screening so the first range fetch matches the week we will show (avoids
 * fetching "today" empty and wiping list residual events).
 *
 * @param {Array<{ start_at?: *, start?: * }>} screenings
 * @param {Date|string|moment.Moment} [from]
 * @returns {Date}
 */
export const screenings_calendar_initial_date = (screenings, from = new Date()) => {
    const events = (screenings || []).map((screening) => ({
        start: screening?.start_at ?? screening?.start,
    }));
    return screenings_calendar_focus_date(events, from);
};

/**
 * Time-grid scroll (`scrollToTime`), same RBC hook as Planner.
 * Prefer the first screening on `date`; otherwise 08:00.
 *
 * @param {Array<{ start?: Date }>} events
 * @param {Date|string|moment.Moment} date
 * @returns {Date}
 */
export const screenings_calendar_scroll_to_time = (events, date) => {
    const day = as_moment(date) || moment();
    const on_day = event_starts(events).find((start) => start.isSame(day, 'day'));
    if (on_day)
        return on_day.toDate();
    return day.clone()
        .hours(SCROLL_FALLBACK_HOUR)
        .minutes(0)
        .seconds(0)
        .milliseconds(0)
        .toDate();
};
